import { useEffect, useState } from 'react';
import { create } from 'zustand';

/** Slow mode: when you may next send a message in each channel (ms timestamps). */
export const useSlowMode = create<{ until: Record<string, number>; start: (channelId: string, seconds: number) => void }>((set) => ({
  until: {},
  start: (channelId, seconds) => {
    if (seconds <= 0) return;
    set((s) => ({ until: { ...s.until, [channelId]: Date.now() + seconds * 1000 } }));
  },
}));

/** Seconds left before you can send in a channel again (0 when you can), ticking down. */
export function useSlowModeWait(channelId: string | null | undefined): number {
  const until = useSlowMode((s) => (channelId ? (s.until[channelId] ?? 0) : 0));
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until <= Date.now()) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= until) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  }, [until]);
  return Math.max(0, Math.ceil((until - Math.max(now, Date.now())) / 1000));
}

/** "5s", "2m", "1h 30m" */
export function formatSlowMode(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m${seconds % 60 ? ` ${seconds % 60}s` : ''}`;
  return `${Math.floor(seconds / 3600)}h${seconds % 3600 >= 60 ? ` ${Math.floor((seconds % 3600) / 60)}m` : ''}`;
}
